// src/components/admin/scheduling/schedule_calendar/WeeklyScheduleModal.js
//
// Two modals used by the Schedule calendar:
//   • WeeklyScheduleModal — 7 Mon→Sun day cards with an Open/Closed segmented
//     toggle and Start/End pickers that only show for Open days. Used both for
//     applying a week's pattern to a selected date range and for editing the
//     default schedule ("Set Default").
//   • DayScheduleModal — a compact single-day editor (double-click a day).
//
// Open/Closed toggling cross-fades the time fields against a "Closed" state
// inside a fixed-height area, so there is no layout shift.

import React, { useEffect, useMemo, useState } from "react";
import { buildTimeOptions, formatTimeFromMinutes } from "../timeUtils";
import {
  DAY_KEYS_MONDAY_FIRST,
  normalizeDaySchedule,
  normalizeWeekConfig,
} from "../scheduleSettingsUtils";

const DAY_SHORT = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

const TIME_OPTIONS = buildTimeOptions(6 * 60, 22 * 60, 30);

// ── Shared bits ───────────────────────────────────────────────────────────────

function Overlay({ children, onClose, width = 760 }) {
  return (
    <div style={S.overlay} onMouseDown={onClose}>
      <div style={{ ...S.card, width }} onMouseDown={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function SegmentedToggle({ isOpen, onChange }) {
  return (
    <div style={S.segment}>
      {[
        { key: true, label: "Open" },
        { key: false, label: "Closed" },
      ].map((opt) => {
        const active = isOpen === opt.key;
        return (
          <button
            key={String(opt.key)}
            type="button"
            onClick={() => onChange(opt.key)}
            style={{
              ...S.segmentBtn,
              background: active ? "#fff" : "transparent",
              color: active ? (opt.key ? "#4338ca" : "#6b7280") : "#9ca3af",
              boxShadow: active ? "0 1px 2px rgba(15,23,42,0.12)" : "none",
              fontWeight: active ? 700 : 500,
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function TimePicker({ label, value, onChange, filterFn }) {
  const opts = filterFn ? TIME_OPTIONS.filter(filterFn) : TIME_OPTIONS;
  return (
    <label style={S.pickerWrap}>
      <span style={S.pickerLabel}>{label}</span>
      <select value={value} onChange={(e) => onChange(Number(e.target.value))} style={S.select}>
        {opts.map((t) => (
          <option key={t} value={t}>{formatTimeFromMinutes(t)}</option>
        ))}
      </select>
    </label>
  );
}

// Fixed-height area that cross-fades the Start/End pickers with a "Closed" state.
function TimeFields({ rule, onStart, onEnd, orientation = "vertical" }) {
  const isOpen = rule.isOpen !== false;
  return (
    <div style={{ position: "relative", flex: 1, minHeight: orientation === "vertical" ? 96 : 58 }}>
      <div
        style={{
          display: "flex",
          flexDirection: orientation === "vertical" ? "column" : "row",
          gap: 8,
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? "auto" : "none",
          transition: "opacity 0.16s ease",
        }}
      >
        <TimePicker
          label="Start"
          value={rule.startMin}
          onChange={onStart}
          filterFn={(t) => t <= rule.endMin - 30}
        />
        <TimePicker
          label="End"
          value={rule.endMin}
          onChange={onEnd}
          filterFn={(t) => t >= rule.startMin + 30}
        />
      </div>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: isOpen ? 0 : 1,
          pointerEvents: "none",
          transition: "opacity 0.16s ease",
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 600, color: "#9ca3af", letterSpacing: "0.3px" }}>
          No hours
        </span>
      </div>
    </div>
  );
}

function DayCard({ dayKey, rule, onChange }) {
  const isOpen = rule.isOpen !== false;
  const setOpen = (open) => onChange(dayKey, { ...rule, isOpen: open });
  const setStart = (startMin) => {
    const nextEnd = rule.endMin <= startMin ? Math.min(22 * 60, startMin + 60) : rule.endMin;
    onChange(dayKey, { ...rule, startMin, endMin: nextEnd });
  };
  const setEnd = (endMin) => onChange(dayKey, { ...rule, endMin });

  return (
    <div style={{ ...S.dayCard, background: isOpen ? "#fff" : "#f9fafb" }}>
      <div style={S.dayCardLabel}>{DAY_SHORT[dayKey]}</div>
      <SegmentedToggle isOpen={isOpen} onChange={setOpen} />
      <TimeFields rule={rule} onStart={setStart} onEnd={setEnd} orientation="vertical" />
    </div>
  );
}

// ── WeeklyScheduleModal ─────────────────────────────────────────────────────────

export default function WeeklyScheduleModal({
  open,
  title = "Weekly Schedule",
  subtitle,
  confirmLabel = "Apply",
  initialWeekConfig,
  busy = false,
  onClose,
  onConfirm,
}) {
  const [weekConfig, setWeekConfig] = useState(() => normalizeWeekConfig(initialWeekConfig));

  useEffect(() => {
    if (open) setWeekConfig(normalizeWeekConfig(initialWeekConfig));
  }, [open, initialWeekConfig]);

  const handleDayChange = (dayKey, nextRule) => {
    setWeekConfig((prev) => ({
      ...prev,
      [dayKey]: normalizeDaySchedule(nextRule, prev[dayKey]),
    }));
  };

  if (!open) return null;

  return (
    <Overlay onClose={busy ? () => {} : onClose} width={880}>
      <div style={S.header}>
        <div>
          <div style={S.title}>{title}</div>
          {subtitle ? <div style={S.subtitle}>{subtitle}</div> : null}
        </div>
        <button style={S.closeBtn} onClick={onClose} disabled={busy}>✕</button>
      </div>

      <div style={S.body}>
        <div style={S.weekGrid}>
          {DAY_KEYS_MONDAY_FIRST.map((dayKey) => (
            <DayCard key={dayKey} dayKey={dayKey} rule={weekConfig[dayKey]} onChange={handleDayChange} />
          ))}
        </div>
      </div>

      <div style={S.footer}>
        <button style={S.btn} onClick={onClose} disabled={busy}>Cancel</button>
        <button
          style={{ ...S.btn, ...S.btnPrimary }}
          onClick={() => onConfirm?.(normalizeWeekConfig(weekConfig))}
          disabled={busy}
        >
          {busy ? "Working…" : confirmLabel}
        </button>
      </div>
    </Overlay>
  );
}

// ── DayScheduleModal (single day) ────────────────────────────────────────────────

export function DayScheduleModal({ open, date, initialRule, busy = false, onClose, onConfirm }) {
  const [rule, setRule] = useState(() => normalizeDaySchedule(initialRule));

  useEffect(() => {
    if (open) setRule(normalizeDaySchedule(initialRule));
  }, [open, initialRule]);

  const dateLabel = useMemo(
    () =>
      date instanceof Date
        ? date.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric", year: "numeric" })
        : "",
    [date]
  );

  const update = (patch) => setRule((prev) => normalizeDaySchedule({ ...prev, ...patch }, prev));
  const isOpen = rule.isOpen !== false;

  if (!open || !date) return null;

  return (
    <Overlay onClose={busy ? () => {} : onClose} width={440}>
      <div style={S.header}>
        <div>
          <div style={S.title}>Edit Day</div>
          <div style={S.subtitle}>{dateLabel}</div>
        </div>
        <button style={S.closeBtn} onClick={onClose} disabled={busy}>✕</button>
      </div>

      <div style={S.body}>
        <div style={{ ...S.dayCard, background: isOpen ? "#fff" : "#f9fafb", width: "100%", boxSizing: "border-box" }}>
          <SegmentedToggle isOpen={isOpen} onChange={(o) => update({ isOpen: o })} />
          <TimeFields
            rule={rule}
            onStart={(startMin) =>
              update({ startMin, endMin: rule.endMin <= startMin ? Math.min(22 * 60, startMin + 60) : rule.endMin })
            }
            onEnd={(endMin) => update({ endMin })}
            orientation="horizontal"
          />
        </div>
      </div>

      <div style={S.footer}>
        <button style={S.btn} onClick={onClose} disabled={busy}>Cancel</button>
        <button
          style={{ ...S.btn, ...S.btnPrimary }}
          onClick={() => onConfirm?.(normalizeDaySchedule(rule))}
          disabled={busy}
        >
          {busy ? "Working…" : "Save"}
        </button>
      </div>
    </Overlay>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const S = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1200,
    padding: 20,
  },
  card: {
    maxWidth: "95vw",
    maxHeight: "90vh",
    background: "#fff",
    borderRadius: 16,
    border: "1px solid #e5e7eb",
    boxShadow: "0 18px 50px rgba(15,23,42,0.22)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    padding: "18px 22px 14px",
    borderBottom: "1px solid #f1f2f4",
  },
  title: { fontSize: 18, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.4px" },
  subtitle: { fontSize: 12.5, color: "#6b7280", marginTop: 4 },
  closeBtn: {
    border: "none",
    background: "transparent",
    fontSize: 16,
    color: "#9ca3af",
    cursor: "pointer",
    lineHeight: 1,
    padding: 4,
  },
  body: { padding: "18px 22px", overflowY: "auto", overflowX: "auto", background: "#f8fafc" },
  weekGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, minmax(96px, 1fr))",
    gap: 10,
    minWidth: 720,
  },
  dayCard: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "12px 10px",
  },
  dayCardLabel: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    color: "#334155",
    textAlign: "center",
  },
  segment: {
    display: "flex",
    background: "#eef1f5",
    borderRadius: 9,
    padding: 3,
    gap: 3,
  },
  segmentBtn: {
    flex: 1,
    border: "none",
    borderRadius: 6,
    padding: "6px 4px",
    fontSize: 12,
    cursor: "pointer",
    transition: "background 0.12s, color 0.12s, box-shadow 0.12s",
  },
  pickerWrap: { display: "flex", flexDirection: "column", gap: 3, flex: 1, minWidth: 0 },
  pickerLabel: {
    fontSize: 9.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: "#9ca3af",
  },
  select: {
    width: "100%",
    height: 34,
    borderRadius: 8,
    border: "1px solid #d8dde4",
    background: "#fff",
    fontSize: 12.5,
    fontWeight: 600,
    color: "#1f2937",
    padding: "0 6px",
    cursor: "pointer",
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    padding: "14px 22px",
    borderTop: "1px solid #f1f2f4",
    background: "#fff",
  },
  btn: {
    padding: "9px 18px",
    borderRadius: 10,
    border: "1px solid #d8dde4",
    background: "#fff",
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    cursor: "pointer",
  },
  btnPrimary: {
    background: "#4f46e5",
    borderColor: "#4f46e5",
    color: "#fff",
    fontWeight: 700,
    boxShadow: "0 1px 2px rgba(79,70,229,0.4)",
  },
};
